import { Brain, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'

interface Props {
  text: string
  /** True while the turn is still running — drives the "Thinking…" wording. */
  active: boolean
}

/**
 * The model's summarised reasoning for the in-flight turn.
 *
 * Opus 5 reasons adaptively before it writes anything, so the turn used to open
 * with a long silence. This pins to the bottom of the chat panel rather than
 * scrolling inside the message list: it is never persisted, and rendering it
 * inline made it read as one more tool call in the scrollback. Collapsed by
 * default — the point is a visible signal that work is happening, with the
 * detail available on click.
 */
export function ThinkingPanel({ text, active }: Props) {
  const [expanded, setExpanded] = useState(false)
  if (!text.trim()) return null
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div className="text-muted-foreground flex flex-col py-2 text-xs">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="hover:text-foreground flex w-fit items-center gap-1.5 text-left transition-colors"
      >
        <Brain className={active ? 'h-3.5 w-3.5 shrink-0 animate-pulse' : 'h-3.5 w-3.5 shrink-0'} />
        <span>{active ? 'Thinking…' : 'Thought process'}</span>
        <Chevron className="h-3 w-3 shrink-0 opacity-60" />
      </button>
      {/* Capped: an uncapped trace would swallow the message list as it streams. */}
      {expanded && (
        <div className="bg-muted/40 mt-1.5 max-h-48 overflow-y-auto rounded-md px-2.5 py-2 whitespace-pre-wrap">
          {text}
        </div>
      )}
    </div>
  )
}
